import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
	faCheck,
	faCircleCheck,
	faTriangleExclamation,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Icon, Modal } from "@/components";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";
import { classNames } from "@/utils/classNames";

import {
	useGetPipedriveCompanySyncTriggerQuery,
	useUpdatePipedriveCompanySyncTriggerMutation,
	useGetPipedriveCompanyDealCreationTriggerQuery,
	useUpdatePipedriveCompanyDealCreationTriggerMutation,
	useGetPipedriveConversationSyncQuery,
	useUpdatePipedriveConversationSyncMutation,
	useLazyGetPipedriveOAuthConnectUrlQuery,
	useGetPipedriveOAuthStatusQuery,
	useDisconnectPipedriveOAuthMutation,
} from "@/pages/Private/redux/pipedrive/pipedrive.api";

import { PipedriveSyncTrigger, PipedriveDealCreationTrigger } from "../schema/pipedrive";
import { PipedriveFieldMappingModal } from "./PipedriveFieldMappingModal";
import { PipedriveDealConfigTab } from "./PipedriveDealConfigTab";
import { PipedriveLeadConfigTab } from "./PipedriveLeadConfigTab";
import { PipedriveUserMappingTab } from "./PipedriveUserMappingTab";
import { PipedriveSyncLogsTab } from "./PipedriveSyncLogsTab";
import { PipedriveResyncSection } from "./PipedriveResyncSection";
import { CrmReconnectBanner } from "./CrmReconnectBanner";

enum PipedriveTabs {
	CONNECTION = "CONNECTION",
	SETTINGS = "SETTINGS",
	DEAL_CONFIGURATION = "DEAL_CONFIGURATION",
	LEAD_CONFIGURATION = "LEAD_CONFIGURATION",
	USER_MAPPING = "USER_MAPPING",
	SYNC_LOGS = "SYNC_LOGS",
}

export const PipedriveSettings: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.pipedrive.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [searchParams, setSearchParams] = useSearchParams();
	const currentTab =
		(searchParams.get("pipedriveTab") as PipedriveTabs) || PipedriveTabs.CONNECTION;

	const [isFieldMappingOpen, setIsFieldMappingOpen] = useState(false);
	const [showDisconnectModal, setShowDisconnectModal] = useState(false);

	const [getOAuthConnectUrl, { isLoading: isLoadingOAuthUrl }] =
		useLazyGetPipedriveOAuthConnectUrlQuery();
	const { data: oauthStatus, refetch: refetchOAuthStatus } = useGetPipedriveOAuthStatusQuery();
	const [disconnectOAuth, { isLoading: isDisconnecting }] = useDisconnectPipedriveOAuthMutation();

	const isConnected = oauthStatus?.connected ?? false;
	// The provider refused the stored sign-in: nothing syncs until the company reconnects (PREC-4353).
	const needsReauthorization = isConnected && Boolean(oauthStatus?.needsReauthorization);

	const { data: companySyncTrigger } = useGetPipedriveCompanySyncTriggerQuery(undefined, {
		skip: !isConnected,
	});
	const [updateCompanySyncTrigger] = useUpdatePipedriveCompanySyncTriggerMutation();

	const { data: companyDealCreationTrigger } = useGetPipedriveCompanyDealCreationTriggerQuery(
		undefined,
		{ skip: !isConnected }
	);
	const [updateCompanyDealCreationTrigger] = useUpdatePipedriveCompanyDealCreationTriggerMutation();

	const { data: conversationSync } = useGetPipedriveConversationSyncQuery(undefined, {
		skip: !isConnected,
	});
	const [updateConversationSync] = useUpdatePipedriveConversationSyncMutation();

	const handleTabChange = useCallback(
		(tab: PipedriveTabs) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("pipedriveTab", tab);

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const syncTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: PipedriveSyncTrigger.DISABLED, title: ts("syncTrigger.DISABLED") },
		{ id: PipedriveSyncTrigger.ON_CAMPAIGN_START, title: ts("syncTrigger.ON_CAMPAIGN_START") },
		{ id: PipedriveSyncTrigger.ON_REPLY, title: ts("syncTrigger.ON_REPLY") },
	];

	const handleSyncTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateCompanySyncTrigger({
				trigger: value.id as PipedriveSyncTrigger,
			}).unwrap();
		} catch (error) {
			console.error("Failed to update sync trigger:", error);
		}
	};

	const dealCreationTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: PipedriveDealCreationTrigger.DISABLED, title: ts("dealCreationTrigger.DISABLED") },
		{
			id: PipedriveDealCreationTrigger.ON_RESPONSE,
			title: ts("dealCreationTrigger.ON_RESPONSE"),
		},
		{
			id: PipedriveDealCreationTrigger.ON_POSITIVE_RESPONSE,
			title: ts("dealCreationTrigger.ON_POSITIVE_RESPONSE"),
		},
	];

	const handleDealCreationTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateCompanyDealCreationTrigger({
				trigger: value.id as PipedriveDealCreationTrigger,
				createAsLead: companyDealCreationTrigger?.createAsLead ?? false,
			}).unwrap();
		} catch (error) {
			console.error("Failed to update deal creation trigger:", error);
		}
	};

	const handleCreateAsLeadToggle = async () => {
		const newValue = !(companyDealCreationTrigger?.createAsLead ?? false);

		try {
			await updateCompanyDealCreationTrigger({
				trigger: companyDealCreationTrigger?.trigger ?? PipedriveDealCreationTrigger.DISABLED,
				createAsLead: newValue,
			}).unwrap();
		} catch (error) {
			console.error("Failed to update createAsLead:", error);
		}
	};

	const handleConversationSyncToggle = async () => {
		const newValue = !(conversationSync?.enabled ?? false);

		try {
			await updateConversationSync({ enabled: newValue }).unwrap();
			handleOpenSnackbar(ts("conversationSync.success"));
		} catch {
			handleOpenSnackbar(ts("conversationSync.error"));
		}
	};

	const handleConnectOAuth = async () => {
		try {
			const result = await getOAuthConnectUrl().unwrap();

			window.location.href = result.url;
		} catch {
			handleOpenSnackbar(ts("oauth.connectError"));
		}
	};

	const handleDisconnectOAuth = async () => {
		try {
			await disconnectOAuth().unwrap();
			setShowDisconnectModal(false);
		} catch (error) {
			const status = (error as { status?: number })?.status;

			if (status === 400) {
				setShowDisconnectModal(false);
				handleOpenSnackbar(ts("oauth.alreadyDisconnected"));
				refetchOAuthStatus();

				return;
			}

			console.error("Failed to disconnect OAuth:", error);
			handleOpenSnackbar(ts("oauth.disconnectError"));
		}
	};

	const tabItems = [
		{ key: PipedriveTabs.CONNECTION, label: ts("tabs.connection") },
		{ key: PipedriveTabs.SETTINGS, label: ts("tabs.settings") },
		{ key: PipedriveTabs.DEAL_CONFIGURATION, label: ts("tabs.dealConfiguration") },
		{ key: PipedriveTabs.LEAD_CONFIGURATION, label: ts("tabs.leadConfiguration") },
		{ key: PipedriveTabs.USER_MAPPING, label: ts("tabs.userMapping") },
		{ key: PipedriveTabs.SYNC_LOGS, label: ts("tabs.syncLogs") },
	];

	const connectionContent = (
		<div className="flex flex-col gap-4">
			{isConnected ? (
				<>
					<div className="flex items-center gap-3 p-4 rounded-lg border border-status-success bg-green-50">
						<Icon className="w-5 h-5 text-status-success" icon={faCircleCheck} />
						<div className="flex flex-col">
							<span className="text-sm font-semibold text-status-success">
								{ts("oauth.connectedViaOAuth")}
							</span>
							{oauthStatus?.companyDomain && (
								<span className="text-xs text-text-meta">
									{ts("oauth.companyDomain")}: {oauthStatus.companyDomain}
								</span>
							)}
							{oauthStatus?.pipedriveUserId && (
								<span className="text-xs text-text-meta">
									{ts("oauth.pipedriveUserId")}: {oauthStatus.pipedriveUserId}
								</span>
							)}
							<span className="text-xs text-text-meta">{ts("oauth.tokenRefresh")}</span>
						</div>
					</div>
					<div className="flex flex-row gap-2">
						<div className="flex flex-wrap gap-2">
							<Button
								color={ButtonColor.DANGER_SECONDARY}
								isLoading={isDisconnecting}
								size={ButtonSize.S}
								title={ts("disconnect")}
								onClick={() => setShowDisconnectModal(true)}
							/>
						</div>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("configureFieldMapping")}
								onClick={() => setIsFieldMappingOpen(true)}
							/>
						</div>
					</div>
				</>
			) : (
				<div className="flex flex-col gap-2">
					<Button
						className="max-w-[250px]"
						color={ButtonColor.ACTION}
						isLoading={isLoadingOAuthUrl}
						size={ButtonSize.S}
						title={ts("oauth.connectWithPipedrive")}
						onClick={handleConnectOAuth}
					/>
				</div>
			)}
		</div>
	);

	const settingsContent = (
		<div className="flex flex-col w-full">
			<div className="text-sm text-text-meta mb-3">{ts("syncTrigger.description")}</div>
			<div className="w-full max-w-[400px]">
				<Dropdown
					data={syncTriggerOptions}
					defaultValue={
						companySyncTrigger
							? {
									id: companySyncTrigger.trigger,
									title: ts(`syncTrigger.${companySyncTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleSyncTriggerChange}
					label={ts("syncTrigger.title")}
				/>
			</div>
			<div className="text-sm text-text-meta mb-3 mt-6">
				{ts("dealCreationTrigger.description")}
			</div>
			<div className="w-full max-w-[400px]">
				<Dropdown
					data={dealCreationTriggerOptions}
					defaultValue={
						companyDealCreationTrigger
							? {
									id: companyDealCreationTrigger.trigger,
									title: ts(`dealCreationTrigger.${companyDealCreationTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleDealCreationTriggerChange}
					label={ts("dealCreationTrigger.title")}
				/>
			</div>
			<div className="mt-4">
				<Checkbox
					isChecked={companyDealCreationTrigger?.createAsLead ?? false}
					name="createAsLead"
					onChange={handleCreateAsLeadToggle}
				>
					{ts("dealCreationTrigger.createAsLead")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-1">
					{ts("dealCreationTrigger.createAsLeadHelp")}
				</div>
			</div>
			<div className="mt-6 pt-6 border-t border-border-light">
				<div className="text-sm font-medium text-text mb-2">{ts("conversationSync.title")}</div>
				<Checkbox
					isChecked={conversationSync?.enabled ?? false}
					name="conversationSync"
					onChange={handleConversationSyncToggle}
				>
					{ts("conversationSync.label")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-2">{ts("conversationSync.description")}</div>
			</div>
			<PipedriveResyncSection />
		</div>
	);

	return (
		<>
			<div className="p-4 rounded-xl border border-border-light bg-brand-white">
				<div className="flex items-center justify-between mb-4">
					<div className="text-smd">{ts("title")}</div>
					{needsReauthorization ? (
						<div className="flex items-center gap-1 text-sm text-status-warning">
							<Icon className="w-4 h-4" icon={faTriangleExclamation} />
							{ts("oauth.reconnectRequired")}
						</div>
					) : isConnected ? (
						<div className="flex items-center gap-1 text-sm text-status-success">
							<Icon className="w-4 h-4" icon={faCheck} />
							{ts("connected")}
						</div>
					) : (
						<div className="flex items-center gap-1 text-sm text-text-meta">
							<Icon className="w-4 h-4" icon={faXmark} />
							{ts("notConnected")}
						</div>
					)}
				</div>

				{needsReauthorization && (
					<CrmReconnectBanner
						buttonLabel={ts("oauth.reconnect")}
						description={ts("oauth.reauthDescription")}
						isLoading={isLoadingOAuthUrl}
						title={ts("oauth.reconnectRequired")}
						onReconnect={handleConnectOAuth}
					/>
				)}

				{isConnected ? (
					<>
						<div className="flex border-b border-border-light mb-4">
							{tabItems.map((tab) => (
								<button
									key={tab.key}
									className={classNames(
										"px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer",
										currentTab === tab.key
											? "border-brand-light text-brand-light"
											: "border-transparent text-text-meta hover:text-text hover:border-border-light"
									)}
									type="button"
									onClick={() => handleTabChange(tab.key)}
								>
									{tab.label}
								</button>
							))}
						</div>

						{currentTab === PipedriveTabs.CONNECTION && connectionContent}
						{currentTab === PipedriveTabs.SETTINGS && settingsContent}
						{currentTab === PipedriveTabs.DEAL_CONFIGURATION && <PipedriveDealConfigTab />}
						{currentTab === PipedriveTabs.LEAD_CONFIGURATION && <PipedriveLeadConfigTab />}
						{currentTab === PipedriveTabs.USER_MAPPING && <PipedriveUserMappingTab />}
						{currentTab === PipedriveTabs.SYNC_LOGS && <PipedriveSyncLogsTab />}
					</>
				) : (
					connectionContent
				)}
			</div>

			{isFieldMappingOpen && (
				<PipedriveFieldMappingModal
					isOpen={isFieldMappingOpen}
					onClose={() => setIsFieldMappingOpen(false)}
				/>
			)}

			{showDisconnectModal && (
				<Modal
					handleClose={() => setShowDisconnectModal(false)}
					handleSave={handleDisconnectOAuth}
					isLoading={isDisconnecting}
					isOpened={showDisconnectModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={ts("disconnect")}
					title={ts("oauth.disconnectTitle")}
				>
					<div className="text-sm">{ts("oauth.disconnectBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
